import { Helmet } from "react-helmet-async";

/**
 * Per-page SEO meta tags + JSON-LD structured data.
 *
 * Used on every public-facing route so each idea / category / feed
 * page becomes its own indexable Google asset (instead of every URL
 * serving the same "Ideas Mine" generic shell).
 *
 * Supports:
 *   - <title> + <meta description> per page
 *   - Open Graph (WhatsApp / LinkedIn / Facebook share cards)
 *   - Twitter Card (X share previews)
 *   - <link rel="canonical"> (de-dup signal to Google)
 *   - JSON-LD Article schema with `isAccessibleForFree` for Medium-style
 *     soft-paywalled Pro posts. Google indexes the teaser; the React
 *     app still shows a Pro upsell to actual visitors.
 */
const SITE = "https://ideasmine.co.uk";
const DEFAULT_IMAGE = `${SITE}/logo-full.png`;

export default function SEO({
    title,
    description,
    path = "",
    image = DEFAULT_IMAGE,
    type = "website",          // "article" | "website"
    article = null,            // { author, datePublished, dateModified, section, locked }
    jsonLd = null,             // optional pre-built JSON-LD override
    noindex = false,
}) {
    const fullTitle = title
        ? `${title} · Ideas Mine`
        : "Ideas Mine — Igniting Innovative Solutions";
    const url = `${SITE}${path}`;
    const desc = (description || "Discover real-world problems and validated startup ideas. Post your own, message authors, and use AI to refine concepts.").slice(0, 300);

    // Auto-generate JSON-LD if caller didn't override.
    let schema = jsonLd;
    if (!schema && type === "article" && article) {
        schema = {
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": title,
            "description": desc,
            "image": image,
            "datePublished": article.datePublished,
            "dateModified": article.dateModified || article.datePublished,
            "author": article.author
                ? { "@type": "Person", "name": article.author }
                : undefined,
            "publisher": {
                "@type": "Organization",
                "name": "Ideas Mine",
                "logo": { "@type": "ImageObject", "url": `${SITE}/logo-full.png` },
            },
            "articleSection": article.section || undefined,
            "url": url,
            "mainEntityOfPage": { "@type": "WebPage", "@id": url },
            // Medium-style soft paywall: tell Google "the body is gated"
            // so it still indexes the title + teaser but knows the full
            // content is behind a Pro wall. Industry-standard pattern.
            ...(article.locked && {
                "isAccessibleForFree": false,
                "hasPart": {
                    "@type": "WebPageElement",
                    "isAccessibleForFree": false,
                    "cssSelector": ".paywalled-content",
                },
            }),
        };
    } else if (!schema && type === "website") {
        schema = {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "url": SITE,
            "name": "Ideas Mine",
            "description": desc,
            "potentialAction": {
                "@type": "SearchAction",
                "target": `${SITE}/feed?q={search_term_string}`,
                "query-input": "required name=search_term_string",
            },
        };
    }

    return (
        <Helmet>
            <title>{fullTitle}</title>
            <meta name="description" content={desc} />
            <link rel="canonical" href={url} />
            {noindex && <meta name="robots" content="noindex,nofollow" />}

            {/* Open Graph — WhatsApp / Facebook / LinkedIn / Slack / Discord */}
            <meta property="og:title" content={title || "Ideas Mine"} />
            <meta property="og:description" content={desc} />
            <meta property="og:url" content={url} />
            <meta property="og:type" content={type === "article" ? "article" : "website"} />
            <meta property="og:image" content={image} />
            <meta property="og:site_name" content="Ideas Mine" />

            {/* Twitter / X */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={title || "Ideas Mine"} />
            <meta name="twitter:description" content={desc} />
            <meta name="twitter:image" content={image} />

            {schema && (
                <script type="application/ld+json">
                    {JSON.stringify(schema)}
                </script>
            )}
        </Helmet>
    );
}
